import { useState } from "react";
import { Waves, TreePalm, Hammer, Footprints, Star, Award, Share2, Heart } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { Reveal, Overline } from "@/components/site/Reveal";

const ICONS = [Waves, TreePalm, Hammer, Footprints];

export const TitleBar = ({ prop }) => {
  const { t, pick } = useI18n();
  const key = `saved-${prop?.slug}`;
  const [saved, setSaved] = useState(() => localStorage.getItem(key) === "1");
  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: prop?.name, url: window.location.href });
      else { await navigator.clipboard.writeText(window.location.href); toast.success(t("l.copied")); }
    } catch (e) { /* user cancelled */ }
  };
  const toggle = () => { localStorage.setItem(key, saved ? "0" : "1"); setSaved(!saved); };
  return (
    <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
      <div>
        <Overline>{prop?.location?.area}</Overline>
        <h2 data-testid="property-title" className="mt-3 max-w-3xl font-display text-4xl font-medium leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl">{pick(prop?.title)}</h2>
      </div>
      <div className="flex gap-2">
        <button data-testid="share-button" onClick={share} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium underline-offset-4 hover:bg-ink/5 hover:underline"><Share2 className="h-4 w-4" />{t("l.share")}</button>
        <button data-testid="save-button" onClick={toggle} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium underline-offset-4 hover:bg-ink/5 hover:underline">
          <Heart className={`h-4 w-4 transition-colors ${saved ? "fill-cta text-cta" : ""}`} />{saved ? t("l.saved") : t("l.save")}
        </button>
      </div>
    </div>
  );
};

export const Overview = ({ prop }) => {
  const { t, pick } = useI18n();
  const [more, setMore] = useState(false);
  const desc = pick(prop?.description);
  const hl = t("hl");
  const host = prop?.host || {};
  return (
    <div className="space-y-12">
      <Reveal>
        <h3 data-testid="property-summary" className="font-display text-3xl text-ink">{t("l.entire")}</h3>
        <p className="mt-2 text-[15px] text-muted2">
          {prop?.max_guests} {t("l.guests")} · {prop?.bedrooms} {t("l.bedrooms")} · {prop?.beds} {t("l.beds")} · {prop?.baths} {t("l.baths")}
        </p>
        {prop?.rating > 0 && (
          <div className="mt-6 flex items-center gap-5 rounded-3xl border border-ink/12 bg-white/60 p-5" data-testid="guest-favourite-block">
            <Award className="h-9 w-9 shrink-0 text-accent2" />
            <p className="flex-1 font-display text-xl leading-tight text-ink">{t("l.guestFav")}</p>
            <div className="text-center">
              <p className="text-lg font-semibold">{prop.rating}</p>
              <div className="flex">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-3 w-3 fill-ink" />)}</div>
            </div>
            <div className="border-l border-ink/15 pl-5 text-center">
              <p className="text-lg font-semibold">{prop.reviews_count}</p>
              <p className="text-xs text-muted2">{t("l.reviews")}</p>
            </div>
          </div>
        )}
      </Reveal>

      <Reveal className="flex items-center gap-4 border-y border-ink/10 py-7" >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-ink font-display text-2xl text-gold">{(host.name || "L").slice(0, 1)}</div>
        <div>
          <p data-testid="host-name" className="font-semibold text-ink">{t("l.hostedBy", { name: host.name || "" })}</p>
          <p className="text-sm text-muted2">{host.superhost ? `${t("l.superhost")} · ` : ""}{t("l.years", { n: host.years || 0 })}</p>
        </div>
      </Reveal>

      <div className="grid gap-7 sm:grid-cols-2" data-testid="highlights">
        {Array.isArray(hl) && hl.map(([title, d], i) => {
          const I = ICONS[i % ICONS.length];
          return (
            <Reveal key={i} delay={i * 0.06} className="flex gap-4">
              <I className="mt-1 h-6 w-6 shrink-0 text-forest" strokeWidth={1.5} />
              <div>
                <p className="font-semibold text-ink">{title}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted2">{d}</p>
              </div>
            </Reveal>
          );
        })}
      </div>

      <Reveal className="border-t border-ink/10 pt-10">
        <h3 className="font-display text-3xl text-ink">{t("about.title")}</h3>
        <div data-testid="property-description" className={`relative mt-5 whitespace-pre-line text-[15px] leading-[1.8] text-body2 ${more ? "" : "max-h-[11.5rem] overflow-hidden"}`}>
          {desc}
          {!more && <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-paper to-transparent" />}
        </div>
        <button data-testid="description-toggle" onClick={() => setMore(!more)} className="mt-4 text-sm font-semibold text-ink underline underline-offset-4">{more ? t("about.less") : t("about.more")}</button>
      </Reveal>
    </div>
  );
};
